
import { useEffect } from "react";

import { 
    ActionStatusHelper, 
    GenerateAction, 
    FeedBackType, 
    LONG_PROCESS_TOAST_DURATION, 
    MessageHelper, 
    ResponseHook, 
    UIFeedbackHooks, 
    ExportParams,
    EXPORT_FORMATS,
    openOrDownloadFile,
    DomainType,
    DOMAIN_CONFIGS
} from "@/Shared";
import { ExportFormat } from "@/Domains";


export interface UseGenerateDomainOptions<T> {
    /** Domain label used in messages (e.g. "User", "Contact") */
    domainType: DomainType;

    /** Whether the current user is allowed to generate */
    canGenerate: boolean;

    generateAction : GenerateAction<T>;

    /** UI feedback hooks (toast, confirm) */
    uiFeedbackHooks: UIFeedbackHooks;

    /** Callback executed after a successful generate */
    onSuccess?: () => void;
}


/**
 * useGenerateDomain hook
 *
 * Generic domain-level hook responsible for handling
 * generate workflows in a consistent and reusable way.
 *
 * It orchestrates:
 * - permission checks
 * - confirmation dialog
 * - async generate execution
 * - toast feedback (loading / success / error)
 *
 * This hook is UI-agnostic and domain-agnostic.
 * It relies only on injected callbacks and responses.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export function useGenerateDomain<T>(options: UseGenerateDomainOptions<T>) {
    const {
        domainType,
        canGenerate,
        generateAction,
        uiFeedbackHooks,
        onSuccess,
    } = options;

    const {domainLabel} = DOMAIN_CONFIGS[domainType];
    const { toastHook, confirmHook } = uiFeedbackHooks;
    const {execute, response, setResponse} = generateAction;


    /**
     * Watches the generate async response and triggers
     * the appropriate UI feedback.
     */
    useEffect(() => {
        const { isSucceeded, isFailed } = ActionStatusHelper.is(
            response.status
        );

        if (isSucceeded) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.GENERATING.SUCCESS(domainLabel),
                    type: FeedBackType.SUCCESS,
                },
                onClose: () => {
                    ResponseHook.reset(setResponse);
                    onSuccess?.();
                }
            });
        }

        if (isFailed) {
            toastHook.openToast?.({
                data: {
                    message:
                        response.error ??
                        MessageHelper.GENERATING.ERROR(domainLabel),
                    type: FeedBackType.ERROR,
                },
                onClose: () => ResponseHook.reset(setResponse)
            });
        }
    }, [response.status, response.error]);

    /**
     * Executes the generate workflow:
     * 1. Permission check
     * 2. User confirmation
     * 3. Loading feedback
     * 4. Generate API call
     */
    const executeGenerate = async (params : ExportParams<any>, format : ExportFormat) => {
        if (!canGenerate) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.GENERATING.NOT_AUTHORIZED(domainLabel),
                    type: FeedBackType.WARNING,
                }
            });
            return;
        }

        let confirmMessage = MessageHelper.MODAL.CONFIRM.GENERATING.DEFAULT(domainLabel);
        
        switch (format) {
            case EXPORT_FORMATS.EXCEL:
                confirmMessage = MessageHelper.MODAL.CONFIRM.GENERATING.EXCEL(domainLabel);
                break;
            case EXPORT_FORMATS.PDF:
                confirmMessage = MessageHelper.MODAL.CONFIRM.GENERATING.PDF(domainLabel);
                break;
            default:
                console.warn("Format d'export inconnu");
                break;
        }

        const confirmed = await confirmHook.openConfirm?.({
            title: MessageHelper.MODAL.CONFIRM.TITLE,
            content: confirmMessage,
        });

        if (!confirmed || !execute) {
            return;
        }

        toastHook.openToast?.({
            data: {
                message: MessageHelper.GENERATING.LOADING(domainLabel),
                type: FeedBackType.INFO,
                duration: LONG_PROCESS_TOAST_DURATION
            }
        });

        const data = await execute(params, format);
        if(data) {
            let extension = '.txt';
            switch (format) {
                case EXPORT_FORMATS.EXCEL:
                    extension = 'xlsx';
                    break;
                case EXPORT_FORMATS.CSV:
                case EXPORT_FORMATS.PDF:
                    extension = format;
                    break;
                default:
                    extension = 'txt';
                    break;
            }
            openOrDownloadFile(
                data.blob, 
                data.filename ?? domainType + "-" + new Date().toISOString() + "." + extension
            );
        }
    };

    /**
     * Public API
     */
    return {
        executeGenerate,
    };
}